import { useEffect, useState } from "react";
import { updateFoodSourceDiscovery } from "../../../../api/foodApi";
import { getFoodDiscoveryOutcome } from "../../../../constants/foodDiscoveryOutcomeLabels";

const DISCOVERY_STATUS_OPTIONS = [
  "NOT_STARTED",
  "IN_REVIEW",
  "PUBLIC_PRICE_CONFIRMED",
  "NO_PUBLIC_PRICE_FOUND",
  "LOGIN_REQUIRED",
  "APP_ONLY",
  "BLOCKED_OR_UNSAFE",
  "NEEDS_MANUAL_REVIEW",
];

const ACCESS_METHOD_OPTIONS = [
  "DISCOVERY_PENDING",
  "STATIC_HTML",
  "PUBLIC_JSON",
  "SITEMAP",
  "RSS_OR_FEED",
  "MANUAL_REVIEW",
  "NOT_SUPPORTED",
];

const TERMS_OPTIONS = [
  "NOT_REVIEWED",
  "REVIEW_REQUIRED",
  "ACCEPTABLE",
  "RESTRICTED",
  "UNKNOWN",
];

const RISK_OPTIONS = ["LOW", "MEDIUM", "HIGH", "DO_NOT_INGEST"];

const emptyForm = {
  discoveryStatus: "NOT_STARTED",
  discoveryNotes: "",
  publicPriceAvailable: "",
  locationRequired: "",
  loginRequired: "",
  captchaOrBotProtectionDetected: "",
  appOnly: "",
  termsReviewStatus: "NOT_REVIEWED",
  recommendedAccessMethod: "DISCOVERY_PENDING",
  sampleProductUrl: "",
  sampleCategoryUrl: "",
  robotsTxtUrl: "",
  discoveryCheckedBy: "",
  ingestionPriority: "",
  ingestionRiskLevel: "MEDIUM",
};

function triStateToForm(value) {
  if (value === true) return "yes";
  if (value === false) return "no";
  return "";
}

function formToTriState(value) {
  if (value === "yes") return true;
  if (value === "no") return false;
  return null;
}

function sourceToForm(source) {
  if (!source) return emptyForm;

  return {
    discoveryStatus: source.discoveryStatus || "NOT_STARTED",
    discoveryNotes: source.discoveryNotes || "",
    publicPriceAvailable: triStateToForm(source.publicPriceAvailable),
    locationRequired: triStateToForm(source.locationRequired),
    loginRequired: triStateToForm(source.loginRequired),
    captchaOrBotProtectionDetected: triStateToForm(source.captchaOrBotProtectionDetected),
    appOnly: triStateToForm(source.appOnly),
    termsReviewStatus: source.termsReviewStatus || "NOT_REVIEWED",
    recommendedAccessMethod: source.recommendedAccessMethod || "DISCOVERY_PENDING",
    sampleProductUrl: source.sampleProductUrl || "",
    sampleCategoryUrl: source.sampleCategoryUrl || "",
    robotsTxtUrl: source.robotsTxtUrl || "",
    discoveryCheckedBy: source.discoveryCheckedBy || "",
    ingestionPriority: source.ingestionPriority ?? "",
    ingestionRiskLevel: source.ingestionRiskLevel || "MEDIUM",
  };
}

const FoodSourceDiscoveryReviewPanel = ({ source, onSaved, onClose }) => {
  const [form, setForm] = useState(() => sourceToForm(source));
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    setForm(sourceToForm(source));
    setErrorMessage("");
  }, [source]);

  if (!source) {
    return (
      <aside className="food-discovery-review panel-card">
        <p className="muted">Select a supermarket source to record discovery review notes.</p>
      </aside>
    );
  }

  const handleChange = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setErrorMessage("");

    try {
      const payload = {
        discoveryStatus: form.discoveryStatus,
        discoveryNotes: form.discoveryNotes || null,
        publicPriceAvailable: formToTriState(form.publicPriceAvailable),
        locationRequired: formToTriState(form.locationRequired),
        loginRequired: formToTriState(form.loginRequired),
        captchaOrBotProtectionDetected: formToTriState(form.captchaOrBotProtectionDetected),
        appOnly: formToTriState(form.appOnly),
        termsReviewStatus: form.termsReviewStatus,
        recommendedAccessMethod: form.recommendedAccessMethod,
        sampleProductUrl: form.sampleProductUrl || null,
        sampleCategoryUrl: form.sampleCategoryUrl || null,
        robotsTxtUrl: form.robotsTxtUrl || null,
        discoveryCheckedBy: form.discoveryCheckedBy || null,
        ingestionPriority:
          form.ingestionPriority === "" ? null : Number(form.ingestionPriority),
        ingestionRiskLevel: form.ingestionRiskLevel,
        lastDiscoveredAt: new Date().toISOString(),
      };

      const updated = await updateFoodSourceDiscovery(source.sourceKey, payload);
      onSaved?.(updated);
    } catch (error) {
      setErrorMessage(error.message || "Unable to save discovery review.");
    } finally {
      setSaving(false);
    }
  };

  const outcome = getFoodDiscoveryOutcome(source);

  return (
    <aside className="food-discovery-review panel-card">
      <header className="food-discovery-review__header">
        <div>
          <h2>{source.sourceName}</h2>
          <p className="muted">{source.sourceKey}</p>
        </div>
        {onClose ? (
          <button type="button" className="admin-dashboard__action-btn" onClick={onClose}>
            Close
          </button>
        ) : null}
      </header>

      <div
        className={`food-discovery-review__outcome food-discovery-review__outcome--${outcome.tone}`}
      >
        <strong>Review outcome:</strong> {outcome.label}
        <div className="muted">Recommendation: {outcome.recommendation}</div>
      </div>

      {source.config?.manualReviewInstructions ? (
        <p className="food-discovery-review__instructions muted">
          {source.config.manualReviewInstructions}
        </p>
      ) : null}

      {source.config?.knownPublicEntryUrls?.length ? (
        <div className="food-discovery-review__urls">
          <div className="eyebrow">Suggested entry URLs</div>
          <ul>
            {source.config.knownPublicEntryUrls.map((url) => (
              <li key={url}>
                <a href={url} target="_blank" rel="noreferrer">
                  {url}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <form className="food-discovery-review__form" onSubmit={handleSubmit}>
        <label>
          Discovery status
          <select value={form.discoveryStatus} onChange={handleChange("discoveryStatus")}>
            {DISCOVERY_STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <div className="food-discovery-review__tri-grid">
          {[
            ["publicPriceAvailable", "Public price visible"],
            ["locationRequired", "Location required"],
            ["loginRequired", "Login required"],
            ["appOnly", "App only"],
            ["captchaOrBotProtectionDetected", "Bot/CAPTCHA risk"],
          ].map(([field, label]) => (
            <label key={field}>
              {label}
              <select value={form[field]} onChange={handleChange(field)}>
                <option value="">Unknown</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
          ))}
        </div>

        <label>
          Recommended access method
          <select
            value={form.recommendedAccessMethod}
            onChange={handleChange("recommendedAccessMethod")}
          >
            {ACCESS_METHOD_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <label>
          Terms review
          <select value={form.termsReviewStatus} onChange={handleChange("termsReviewStatus")}>
            {TERMS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <label>
          Ingestion risk
          <select value={form.ingestionRiskLevel} onChange={handleChange("ingestionRiskLevel")}>
            {RISK_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <label>
          Ingestion priority
          <input
            type="number"
            min="1"
            value={form.ingestionPriority}
            onChange={handleChange("ingestionPriority")}
          />
        </label>

        <label>
          Sample category URL
          <input type="url" value={form.sampleCategoryUrl} onChange={handleChange("sampleCategoryUrl")} />
        </label>

        <label>
          Sample product URL
          <input type="url" value={form.sampleProductUrl} onChange={handleChange("sampleProductUrl")} />
        </label>

        <label>
          robots.txt URL
          <input type="url" value={form.robotsTxtUrl} onChange={handleChange("robotsTxtUrl")} />
        </label>

        <label>
          Reviewed by
          <input type="text" value={form.discoveryCheckedBy} onChange={handleChange("discoveryCheckedBy")} />
        </label>

        <label>
          Notes
          <textarea
            rows={4}
            value={form.discoveryNotes}
            onChange={handleChange("discoveryNotes")}
          />
        </label>

        {errorMessage ? <p className="food-discovery-review__error">{errorMessage}</p> : null}

        <p className="muted food-discovery-review__notice">
          Saving discovery metadata does not enable ingestion. All supermarket sources remain
          disabled until a future milestone.
        </p>

        <button type="submit" className="crm-btn crm-btn-primary" disabled={saving}>
          {saving ? "Saving…" : "Save discovery review"}
        </button>
      </form>
    </aside>
  );
};

export default FoodSourceDiscoveryReviewPanel;
